import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getComparisonPeriod, ComparisonPeriod } from '@/utils/periodComparison';
import { startOfDayInTz, endOfDayInTz, formatDateInputInTz } from '@/utils/timezone';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';
import { taxFromGross, grossFromNet } from '@/utils/tax';

/**
 * Lightweight metrics structure for comparison.
 * Contains only the essential values needed for period-over-period comparison.
 */
export interface ComparisonMetrics {
  // Lead metrics
  totalLeads: number;
  openLeads: number;
  followUpLeads: number;
  convertedLeads: number;
  lostLeads: number;
  
  // Job metrics
  totalJobs: number;
  scheduledJobs: number;
  inProgressJobs: number;
  onHoldJobs: number;
  completedJobs: number;
  cancelledJobs: number;
  
  // Financial metrics
  totalTurnover: number;
  netProfit: number;
  techPay: number;
  partsCost: number;
  tollCost: number;
  campaignCost: number;
  gstOwed: number;
}

export interface ComparisonData {
  metrics: ComparisonMetrics;
  period: ComparisonPeriod;
}

/**
 * Hook to fetch comparison metrics for the previous period.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Only enabled when main data is loaded (deferred loading)
 * - Uses parallel queries with Promise.all
 * - Long staleTime (5 min) since historical data doesn't change
 * - Fetches only essential fields, no JOINs or complex aggregations
 */
export const useComparisonMetrics = (
  startDate: Date,
  endDate: Date,
  timezone: string,
  enabled: boolean = true
) => {
  const comparisonPeriod = getComparisonPeriod(startDate, endDate);
  const tax = useCompanyTax();
  const { canSeeCompanyFinancials } = useVisibility();

  return useQuery({
    queryKey: ['comparison-metrics', comparisonPeriod.start.toISOString(), comparisonPeriod.end.toISOString(), timezone, tax.rate, tax.enabled],
    queryFn: async (): Promise<ComparisonData> => {
      const start = startOfDayInTz(comparisonPeriod.start, timezone).toISOString();
      const end = endOfDayInTz(comparisonPeriod.end, timezone).toISOString();
      const startDateStr = formatDateInputInTz(comparisonPeriod.start, timezone);
      const endDateStr = formatDateInputInTz(comparisonPeriod.end, timezone);
      
      // PERFORMANCE: Run all queries in parallel
      const [leadsResult, jobsByScheduleResult, jobsByCloseResult, spendResult] = await Promise.all([
        // Leads: filter by created_at (for lead cohort metrics)
        supabase
          .from('leads')
          .select('id, status, job_id')
          .gte('created_at', start)
          .lte('created_at', end),
        
        // Jobs by scheduled_date (for job schedule metrics)
        supabase
          .from('jobs')
          .select('id, status')
          .gte('scheduled_date', start)
          .lte('scheduled_date', end),
        
        // Jobs by job_date (for financial metrics)
        supabase
          .from('jobs')
          .select('id, status, invoice_total, company_profit, tech_commission_amount, total_parts_cost, toll_cost, total_override_commission')
          .gte('job_date', startDateStr)
          .lte('job_date', endDateStr),
        
        // Campaign spend
        supabase
          .from('campaign_daily_spend')
          .select('actual_cost')
          .gte('spend_date', startDateStr)
          .lte('spend_date', endDateStr),
      ]);
      
      // Check for errors
      if (leadsResult.error) throw leadsResult.error;
      if (jobsByScheduleResult.error) throw jobsByScheduleResult.error;
      if (jobsByCloseResult.error) throw jobsByCloseResult.error;
      if (spendResult.error) throw spendResult.error;
      
      // Process leads
      const leads = leadsResult.data || [];
      const totalLeads = leads.length;
      const openLeads = leads.filter(l => l.status === 'new').length;
      const followUpLeads = leads.filter(l => l.status === 'needs_follow_up').length;
      const convertedLeads = leads.filter(l => l.job_id !== null).length;
      const lostLeads = leads.filter(l => l.status === 'lost').length;
      
      // Process jobs by schedule (for job metrics)
      const jobsBySchedule = jobsByScheduleResult.data || [];
      const totalJobs = jobsBySchedule.length;
      const scheduledJobs = jobsBySchedule.filter(j => j.status === 'scheduled').length;
      const inProgressJobs = jobsBySchedule.filter(j => j.status === 'in_progress').length;
      const onHoldJobs = jobsBySchedule.filter(j => j.status === 'on_hold').length;
      const completedJobs = jobsBySchedule.filter(j => j.status === 'completed').length;
      const cancelledJobs = jobsBySchedule.filter(j => j.status === 'cancelled').length;
      
      // Process jobs by close date (for financial metrics)
      const closedJobs = (jobsByCloseResult.data || []).filter(j => j.status === 'completed');
      const spend = spendResult.data || [];
      
      const totalTurnover = closedJobs.reduce((sum, j) => sum + (j.invoice_total || 0), 0);
      const gstOwed = taxFromGross(totalTurnover, tax);
      const techPay = closedJobs.reduce((sum, j) => sum + (j.tech_commission_amount || 0), 0);
      const partsCost = closedJobs.reduce((sum, j) => sum + (j.total_parts_cost || 0), 0);
      const tollCost = closedJobs.reduce((sum, j) => sum + (j.toll_cost || 0), 0);
      const overrideCommission = closedJobs.reduce((sum, j) => sum + (j.total_override_commission || 0), 0);
      const campaignCostExGst = spend.reduce((sum, s) => sum + (s.actual_cost || 0), 0);
      const campaignCost = grossFromNet(campaignCostExGst, tax);
      
      // Net profit: company_profit - override_commission - campaign_cost
      const companyProfit = closedJobs.reduce((sum, j) => sum + (j.company_profit || 0), 0);
      const netProfit = companyProfit - overrideCommission - campaignCost;
      
      return {
        metrics: {
          totalLeads,
          openLeads,
          followUpLeads,
          convertedLeads,
          lostLeads,
          totalJobs,
          scheduledJobs,
          inProgressJobs,
          onHoldJobs,
          completedJobs,
          cancelledJobs,
          totalTurnover,
          netProfit,
          techPay,
          partsCost,
          tollCost,
          campaignCost,
          gstOwed,
        },
        period: comparisonPeriod,
      };
    },
    enabled: enabled && canSeeCompanyFinancials, // Only fetch when main data is loaded
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (historical data doesn't change)
    gcTime: 10 * 60 * 1000, // Keep in garbage collection for 10 minutes
  });
};
